import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  BarChart3,
  Settings,
  User,
  ClipboardList,
  FileText,
  LogOut,
  X,
  Award,
} from "lucide-react";
import { useAuthStore } from "./stores/authStore";
import { authService } from "./services/authService";

interface SidebarProps {
  userRole?: 'admin' | 'employee';
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ userRole, isOpen = true, onClose }) => {
  const [path, setPath] = useState<string>('');
  const [sidebarHeight, setSidebarHeight] = useState<string>('100vh');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  // Use user role from auth store if not provided as prop
  const currentUserRole = userRole || user?.role;

  useEffect(() => {
    let pathSegments = location.pathname.split('/');
    if (pathSegments[1] === 'admin') {
      setPath('/admin' + (pathSegments[2] ? `/${pathSegments[2]}` : ''));
    } else {
      // For employee routes, use the full path
      setPath(location.pathname);
    }
  }, [location.pathname]);

  // Calculate sidebar height for mobile devices
  useEffect(() => {
    const getMobileViewportHeight = () => {
      if (window.innerWidth < 1024) { // Mobile/tablet screens
        // Use the actual viewport height including safe areas
        return `${window.innerHeight}px`;
      }
      return '100vh';
    };

    const updateHeight = () => setSidebarHeight(getMobileViewportHeight());
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Admin navigation
  const adminNav = [
    {
      icon: <Home className="w-6 h-6" />,
      name: "Dashboard",
      link: "/admin",
    },
    {
      icon: <Users className="w-6 h-6" />,
      name: "Employees",
      link: "/admin/employees",
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      name: "Courses",
      link: "/admin/courses",
    },
    {
      icon: <Award className="w-6 h-6" />,
      name: "Certificates",
      link: "/admin/certificates",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      name: "Transcripts",
      link: "/admin/transcripts",
    },
  ];

  // Employee navigation
  const employeeNav = [
    {
      icon: <Home className="w-6 h-6" />,
      name: "Dashboard",
      link: "/dashboard",
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      name: "My Courses",
      link: "/courses",
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      name: "My Certificates",
      link: "/certificates",
    },
  ];

  const nav = currentUserRole === 'admin' ? adminNav : employeeNav;

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await authService.logout();
      
      // Clear auth store
      clearAuth();
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state and redirect
      clearAuth();
      navigate('/login');
    }
  };

  return (
    <div className={`fixed bg-primary-600 w-64 flex flex-col overflow-y-auto px-2 pt-5 border-r border-primary-700 z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
      isOpen ? 'translate-x-0' : '-translate-x-full'
    }`}
    style={{ height: sidebarHeight }}>
      <div className="mb-10">
        {/* sidebar header */}
        <div className="flex items-center justify-between px-2 py-px mb-10">
          <Link to="/" className="flex items-center gap-2.5">
            <h2 className="font-black text-2xl leading-8 text-white">CareLMS</h2>
          </Link>
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden text-white hover:text-primary-200 p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* navigation */}
        <nav className="flex flex-col gap-1">
          {nav.map((item, idx) => (
            <Link
              className={`${
                path === item.link
                  ? "bg-primary-500 text-white"
                  : "bg-transparent"
              } flex items-center font-medium gap-3 p-2 pr-3 rounded-md hover:bg-primary-500 group`}
              key={idx}
              to={item.link}
              onClick={() => {
                // Close sidebar on mobile when navigating
                if (onClose) {
                  onClose();
                }
              }}
            >
              <span className={path === item.link ? "text-white" : "text-primary-100 group-hover:text-white"}>
                {item.icon}
              </span>
              <span className={`${path === item.link ? "text-white" : "text-primary-100"} group-hover:text-white text-sm capitalize`}>
                {item.name}
              </span>
            </Link>
          ))}
        </nav>
      </div>
      
      <div className="mt-auto">
        <div className="mb-2.5 pb-4 pt-6">
          {/* User profile section */}
          <div className="flex items-center gap-3 p-2 mb-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-primary-400 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-primary-200 truncate">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          </div>
          
          {/* Bottom navigation */}
          <nav className="flex flex-col gap-1">
            <Link 
              to={userRole === 'admin' ? '/admin/profile' : '/profile'} 
              className={`${path === '/profile' || path === '/admin/profile' ? "bg-primary-500 text-white" : "bg-transparent"} flex items-center font-medium gap-3 p-2 pr-3 rounded-md hover:bg-primary-500 group`}
              onClick={() => {
                if (onClose) {
                  onClose();
                }
              }}
            >
              <span className={path === '/profile' || path === '/admin/profile' ? "text-white" : "text-primary-100 group-hover:text-white"}>
                <Settings className="w-6 h-6" />
              </span>
              <span className={`${path === '/profile' || path === '/admin/profile' ? "text-white" : "text-primary-100"} group-hover:text-white text-sm capitalize`}>
                settings
              </span>
            </Link>
            
            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="flex items-center font-medium gap-3 p-2 pr-3 rounded-md hover:bg-primary-500 group bg-transparent w-full text-left"
            >
              <span className="text-primary-100 group-hover:text-white">
                <LogOut className="w-6 h-6" />
              </span>
              <span className="text-primary-100 group-hover:text-white text-sm capitalize">
                logout
              </span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};